import { useParams } from 'react-router-dom';
import {
  Button,
  H3,
  Icon,
  If,
  P,
  Stack,
  useRelativeTime,
} from '@fashionnovateam/nova-ui-toolkit';
import { useEffect, useMemo, useState } from 'react';
import { useGetVendorItem } from '../../../../Lib/Hooks/ItemService';
import { ItemFilters } from '../../../../Lib/Hooks/ItemService/enum';
import { ItemKeyDetailsReadOnly } from './ItemKeyDetailsReadOnly';
import { ItemKeyDetailsEditForm } from './ItemKeyDetailsEditForm';
import SampleTagsAPI from '../../../../Lib/Api/sample-tags';
import { downloadVendorCatalogPDF } from '../../../SampleTags/AddTags/utils/pdfUtils';

export function ItemKeyDetails() {
  const { id: currentUserId, itemId: currentItemId } = useParams<{
    id: string;
    itemId: string;
  }>();
  const [mode, setMode] = useState<'read' | 'edit'>('read');
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  const { data: targetItem } = useGetVendorItem(currentUserId, currentItemId);
  const { updated_at: updatedAt } = targetItem || {};

  const relativeTime = useRelativeTime(
    updatedAt ? new Date(updatedAt).getTime() : 0,
  );

  useEffect(() => {
    // Reset mode to read when navigating to another item
    setMode('read');
  }, [targetItem?.id]);

  const canEdit = useMemo(() => {
    if (!targetItem) {
      return false;
    }
    return (
      targetItem.stage === ItemFilters.PENDING_VENDOR ||
      targetItem.stage === ItemFilters.NEEDS_REVIEW
    );
  }, [targetItem]);

  const handleDownloadSampleTag = async () => {
    if (!currentUserId || !targetItem?.id) {
      return;
    }

    setIsGeneratingPDF(true);
    try {
      const sampleTag = await SampleTagsAPI.getOrCreateFromVendorCatalogItem(
        currentUserId,
        targetItem.id,
      );

      await downloadVendorCatalogPDF(sampleTag, currentUserId);
    } catch (error) {
      console.error('Failed to download sample tag PDF:', error);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  if (!targetItem) {
    return null;
  }

  return (
    <Stack direction="col" gap="sm" className="p-4">
      <div className="flex flex-col gap-1">
        <div className="flex justify-between items-center mb-2 gap-1">
          <div className="flex flex-col gap-1">
            <H3>{targetItem.style_number}</H3>
            <If condition={mode === 'read'}>
              <div className="flex flex-row gap-1">
                <div className="flex flex-row items-center nu-text-base-disabled">
                  <P size="xs" color="disabled" weight="medium">
                    {targetItem.division || '-'}
                  </P>
                  <Icon.ChevronRight />
                  <P size="xs" color="disabled" weight="medium">
                    {targetItem.department || '-'}
                  </P>
                  <Icon.ChevronRight />
                  <P size="xs" color="disabled" weight="medium">
                    {targetItem.category || '-'}
                  </P>
                  <Icon.ChevronRight />
                  <P size="xs" color="disabled" weight="medium">
                    {targetItem.class || '-'}
                  </P>
                </div>
              </div>
            </If>
          </div>
          <If condition={mode === 'read'}>
            <div className="flex flex-row items-center gap-3 ml-0">
              <P size="xs" color="disabled">
                {relativeTime}
              </P>
              <Button
                color="secondary"
                onClick={handleDownloadSampleTag}
                disabled={isGeneratingPDF}
              >
                <Icon name="download/download-down" variant="solid" />
                {isGeneratingPDF ? 'Generating...' : 'Sample Tag'}
              </Button>
              {canEdit && (
                <Button color="secondary" onClick={() => setMode('edit')}>
                  <Icon name="pencil-edit/pencil-edit-line" variant="solid" />
                  Edit
                </Button>
              )}
            </div>
          </If>
        </div>
      </div>
      <If condition={mode === 'read'}>
        <ItemKeyDetailsReadOnly />
      </If>
      <If condition={mode === 'edit'}>
        <ItemKeyDetailsEditForm
          item={targetItem}
          onCloseForm={() => setMode('read')}
        />
      </If>
    </Stack>
  );
}
